/**
 * Serialization between the canonical "reference" form of a text field and the
 * human-readable "display" form shown in the input.
 *
 * - Reference (canonical, stored/submitted):
 *     "Hi @{ref:user::id:1337::name:John Smith}, welcome."
 * - Display (what the user sees and edits):
 *     "Hi @John Smith, welcome."
 *
 * Mentions are tracked by their position (range) in the display string, never
 * by matching on the name — this keeps duplicate names unambiguous and survives
 * arbitrary editing of the surrounding text.
 */

export interface MentionRange {
  /** User (or other entity) id, as a string to match the ref grammar. */
  id: string;
  /** Human-readable name shown after the `@`. */
  name: string;
  /** Reference type, e.g. `user`. */
  refType: string;
  /** Start index of the `@` in display coordinates. */
  start: number;
  /** End index (exclusive) in display coordinates. */
  end: number;
}

export interface ParsedValue {
  display: string;
  mentions: MentionRange[];
}

/**
 * Canonical reference token, e.g. `@{ref:user::id:1337::name:John Smith}`.
 * `name` greedily consumes everything up to the closing brace, so names may
 * contain spaces and `::` but not `}` (real display names never do).
 */
const REF_TOKEN = /@\{ref:(\w+)::id:(\d+)::name:([^}]+)\}/g;

export function formatRef(
  mention: Pick<MentionRange, 'refType' | 'id' | 'name'>
): string {
  return `@{ref:${mention.refType}::id:${mention.id}::name:${mention.name}}`;
}

/**
 * Convert a canonical reference string into its display string plus the set of
 * mention ranges (in display coordinates). Plain text — including a literal
 * `@something` the user typed but never resolved to a mention — is preserved
 * verbatim and produces no mention range.
 */
export function parse(full: string): ParsedValue {
  const mentions: MentionRange[] = [];
  let display = '';
  let lastIndex = 0;

  // A fresh RegExp instance per call keeps `lastIndex` state local.
  const regex = new RegExp(REF_TOKEN);
  let match: RegExpExecArray | null;
  while ((match = regex.exec(full)) !== null) {
    const [token, refType, id, name] = match;
    display += full.slice(lastIndex, match.index);
    const start = display.length;
    display += `@${name}`;
    const end = display.length;
    mentions.push({ id, name, refType, start, end });
    lastIndex = match.index + token.length;
  }
  display += full.slice(lastIndex);

  return { display, mentions };
}

/**
 * Inverse of {@link parse}: rebuild the canonical reference string by replacing
 * each mention's display range with its reference token. Ranges must be
 * non-overlapping and within the display bounds (the invariants `parse`,
 * `reconcile`, and the atomic-delete helper all maintain).
 *
 * Invariant: `build(parse(full).display, parse(full).mentions) === full` for any
 * well-formed `full`.
 */
export function build(display: string, mentions: MentionRange[]): string {
  const sorted = [...mentions].sort((a, b) => a.start - b.start);
  let full = '';
  let cursor = 0;
  for (const mention of sorted) {
    full += display.slice(cursor, mention.start);
    full += formatRef(mention);
    cursor = mention.end;
  }
  full += display.slice(cursor);
  return full;
}
